// Shop ticker under the navbar — promotions and updates (not a blocking popup).

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getFirestore, collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { matchesTargeting, trackAnnouncementView, trackAnnouncementClick } from '../services/announcementAnalytics';
import {
    markTickerItemsHidden,
    readHiddenTickerIds,
} from '../utils/announcementDisplayStorage';
import {
    buildTickerItems,
    applyTickerOpenClass,
    LSC_TICKER_MOTION_MS,
    stepTickerPresence,
    tickerAudienceFromUser,
    tickerLoopDurationSec,
} from '../utils/announcementTickerUtils';
import { resolvePricingKeyFromUserData } from '../services/pricingTierService';
import './AnnouncementTicker.css';

const db = getFirestore();

function shouldHideShopTicker(pathname) {
    return /^\/(packer|production-toolkit)/i.test(pathname) || /^\/s\//i.test(pathname);
}

function isInternalPath(href) {
    return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
}

function setTickerOpenClass(open) {
    applyTickerOpenClass(open);
}

function TickerItemInner({ item }) {
    return (
        <>
            <span className="lsc-site-ticker__text">{item.text}</span>
            {item.ctaText ? <span className="lsc-site-ticker__cta">{item.ctaText}</span> : null}
        </>
    );
}

export default function AnnouncementTicker() {
    const location = useLocation();
    const { user } = useAuth();
    const [userData, setUserData] = useState(null);
    const [rawAnnouncements, setRawAnnouncements] = useState([]);
    const [globalEnabled, setGlobalEnabled] = useState(true);
    const [hiddenIds, setHiddenIds] = useState(() => readHiddenTickerIds());
    const [paused, setPaused] = useState(false);
    const [phase, setPhase] = useState('out');
    const [heldItems, setHeldItems] = useState([]);
    const [dismissing, setDismissing] = useState(false);
    const pendingHideIdsRef = useRef(null);
    const trackedIdsRef = useRef(new Set());
    const hideChrome = shouldHideShopTicker(location.pathname);
    const isoDate = useMemo(() => new Date().toISOString().split('T')[0], []);
    const audience = useMemo(() => tickerAudienceFromUser(userData), [userData]);

    useEffect(() => {
        if (!user?.uid) {
            setUserData(null);
            return;
        }
        let cancelled = false;
        getDoc(doc(db, 'users', user.uid))
            .then((snap) => {
                if (cancelled) return;
                if (snap.exists()) {
                    setUserData({ uid: user.uid, ...snap.data() });
                } else {
                    setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
                }
            })
            .catch((error) => {
                console.error('Error fetching user data for ticker:', error);
                if (!cancelled) {
                    setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [user?.uid]);

    useEffect(() => {
        getDoc(doc(db, 'system_settings', 'announcements'))
            .then((settingsDoc) => {
                if (settingsDoc.exists() && settingsDoc.data().enabled === false) {
                    setGlobalEnabled(false);
                }
            })
            .catch((error) => {
                console.error('Error checking ticker global settings:', error);
            });
    }, []);

    useEffect(() => {
        if (!globalEnabled || hideChrome) {
            setRawAnnouncements([]);
            return undefined;
        }

        const announcementsQuery = query(collection(db, 'announcements'), where('active', '==', true));
        const unsubscribe = onSnapshot(
            announcementsQuery,
            (snapshot) => {
                const list = [];
                snapshot.forEach((d) => {
                    list.push({ id: d.id, ...d.data() });
                });
                setRawAnnouncements(list);
            },
            (error) => {
                console.error('Error listening to ticker announcements:', error);
            }
        );
        return () => unsubscribe();
    }, [globalEnabled, hideChrome]);

    const liveItems = useMemo(() => {
        if (!globalEnabled || hideChrome) return [];
        if (user?.uid && !userData) return [];
        const built = buildTickerItems(rawAnnouncements, { isoDate, hiddenIds });
        return built.filter((item) => {
            const source = rawAnnouncements.find((a) => a.id === item.id);
            return source ? matchesTargeting(source, audience) : false;
        });
    }, [rawAnnouncements, hiddenIds, globalEnabled, hideChrome, audience, isoDate, user, userData]);

    const wantOpen = liveItems.length > 0 && !hideChrome && globalEnabled && !dismissing;
    const displayItems = liveItems.length ? liveItems : heldItems;

    useEffect(() => {
        if (liveItems.length) {
            setHeldItems(liveItems);
        }
    }, [liveItems]);

    useEffect(() => {
        const next = stepTickerPresence(phase, wantOpen);
        if (next !== phase) {
            setPhase(next);
        }
    }, [wantOpen, phase]);

    useLayoutEffect(() => {
        if (phase === 'enter') {
            setTickerOpenClass(false);
            let frame2 = 0;
            const frame1 = window.requestAnimationFrame(() => {
                frame2 = window.requestAnimationFrame(() => {
                    setTickerOpenClass(true);
                    setPhase('in');
                });
            });
            return () => {
                window.cancelAnimationFrame(frame1);
                window.cancelAnimationFrame(frame2);
            };
        }
        if (phase === 'in') {
            setTickerOpenClass(true);
            return undefined;
        }
        setTickerOpenClass(false);
        return undefined;
    }, [phase]);

    useEffect(() => {
        if (phase !== 'leave') return undefined;
        const done = () => {
            const ids = pendingHideIdsRef.current;
            if (ids && ids.length) {
                markTickerItemsHidden(ids);
                setHiddenIds(readHiddenTickerIds());
                pendingHideIdsRef.current = null;
            }
            setDismissing(false);
            setHeldItems([]);
            setPhase('out');
        };
        const reduceMotion =
            (typeof document !== 'undefined' &&
                document.documentElement.classList.contains('app-reduced-motion')) ||
            (typeof window !== 'undefined' &&
                window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        const timer = window.setTimeout(done, reduceMotion ? 80 : LSC_TICKER_MOTION_MS);
        return () => window.clearTimeout(timer);
    }, [phase]);

    useEffect(() => {
        return () => {
            setTickerOpenClass(false);
        };
    }, []);

    const loopTextLength = displayItems.reduce(
        (sum, item) => sum + item.text.length + (item.ctaText ? item.ctaText.length + 2 : 0),
        0
    );
    const durationSec = tickerLoopDurationSec(loopTextLength);

    useEffect(() => {
        if (!liveItems.length) return;
        liveItems.forEach((item) => {
            if (trackedIdsRef.current.has(item.id)) return;
            trackedIdsRef.current.add(item.id);
            trackAnnouncementView(
                item.id,
                userData?.uid,
                audience.role,
                resolvePricingKeyFromUserData(audience),
                'A'
            ).catch((err) => console.warn('[AnnouncementTicker] view track failed', err));
        });
    }, [liveItems, userData, audience]);

    const handleHide = useCallback(() => {
        if (phase === 'leave' || phase === 'out' || dismissing) return;
        pendingHideIdsRef.current = displayItems.map((item) => item.id);
        setHeldItems(displayItems);
        setDismissing(true);
    }, [phase, dismissing, displayItems]);

    const handleItemActivate = useCallback(
        (item) => {
            if (!item.ctaLink) return;
            trackAnnouncementClick(item.id, userData?.uid, audience.role, item.ctaLink, 'A').catch(
                (err) => console.warn('[AnnouncementTicker] click track failed', err)
            );
        },
        [userData, audience]
    );

    if (phase === 'out' || displayItems.length === 0) {
        return null;
    }

    const clipClass = [
        'lsc-site-ticker-clip',
        phase === 'in' ? 'is-in' : '',
        phase === 'leave' ? 'is-leave' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const renderCopy = (copyIndex) => (
        <div className="lsc-site-ticker__copy" key={`copy-${copyIndex}`}>
            {displayItems.map((item) => {
                const inner = <TickerItemInner item={item} />;
                const sep = <span className="lsc-site-ticker__sep" aria-hidden="true" />;
                if (item.ctaLink && isInternalPath(item.ctaLink)) {
                    return (
                        <span className="lsc-site-ticker__item" key={`${copyIndex}-${item.id}`}>
                            <Link
                                className="lsc-site-ticker__link"
                                to={item.ctaLink}
                                tabIndex={-1}
                                onClick={() => handleItemActivate(item)}
                            >
                                {inner}
                            </Link>
                            {sep}
                        </span>
                    );
                }
                if (item.ctaLink) {
                    return (
                        <span className="lsc-site-ticker__item" key={`${copyIndex}-${item.id}`}>
                            <a
                                className="lsc-site-ticker__link"
                                href={item.ctaLink}
                                tabIndex={-1}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleItemActivate(item)}
                            >
                                {inner}
                            </a>
                            {sep}
                        </span>
                    );
                }
                return (
                    <span className="lsc-site-ticker__item" key={`${copyIndex}-${item.id}`}>
                        {inner}
                        {sep}
                    </span>
                );
            })}
        </div>
    );

    return (
        <div className={clipClass}>
            <div className="lsc-site-ticker-clip__cell">
                <div
                    className={`lsc-site-ticker${paused ? ' is-paused' : ''}`}
                    role="region"
                    aria-label="London Smoke and Cure updates"
                    onPointerDown={() => setPaused(true)}
                    onPointerUp={() => setPaused(false)}
                    onPointerCancel={() => setPaused(false)}
                    onMouseLeave={() => setPaused(false)}
                >
                    <span className="lsc-site-ticker__sheen" aria-hidden="true" />
                    <ul className="lsc-site-ticker__sr">
                        {displayItems.map((item) => (
                            <li key={`sr-${item.id}`}>
                                {item.ctaLink ? (
                                    isInternalPath(item.ctaLink) ? (
                                        <Link to={item.ctaLink} onClick={() => handleItemActivate(item)}>
                                            {item.text}
                                            {item.ctaText ? ` ${item.ctaText}` : ''}
                                        </Link>
                                    ) : (
                                        <a
                                            href={item.ctaLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => handleItemActivate(item)}
                                        >
                                            {item.text}
                                            {item.ctaText ? ` ${item.ctaText}` : ''}
                                        </a>
                                    )
                                ) : (
                                    item.text
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="lsc-site-ticker__viewport" aria-hidden="true">
                        <div
                            className="lsc-site-ticker__track"
                            style={{ animationDuration: `${durationSec}s` }}
                        >
                            {renderCopy(0)}
                            {renderCopy(1)}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="lsc-site-ticker__close"
                        onClick={handleHide}
                        aria-label="Hide updates banner for this visit"
                    >
                        <CloseOutlined />
                    </button>
                </div>
            </div>
        </div>
    );
}
